import type { CreatorListItemServer } from '@metapic-apps/shared'

import { normalizeCreatorListItem } from '@/lib/adapters/creators.adapter'
import type {
  CreatorDetail,
  CreatorDetailRaw,
  CreatorExtraInfo,
  CreatorExtraInfoRaw,
  CreatorStatusAction,
  CreatorUpdatePayload,
} from '@/lib/types/creator-detail.types'

export const deriveStatusAction = (raw: CreatorDetailRaw): CreatorStatusAction => {
  if (raw.is_suspended && raw.remove_earnings) {
    return 'banAndRemoveEarnings'
  }

  if (raw.is_suspended) {
    return 'banned'
  }

  if (raw.payment_blocked) {
    return 'blockPayments'
  }

  if (raw.is_verified) {
    return 'approved'
  }

  return 'notApproved'
}

export const serializeStatusAction = (
  action: CreatorStatusAction,
): Pick<
  CreatorUpdatePayload,
  'is_verified' | 'is_suspended' | 'payment_blocked' | 'remove_earnings'
> => {
  switch (action) {
    case 'approved':
      return {
        is_verified: true,
        is_suspended: false,
        payment_blocked: false,
        remove_earnings: false,
      }
    case 'banned':
      return {
        is_verified: false,
        is_suspended: true,
        payment_blocked: false,
        remove_earnings: false,
      }
    case 'blockPayments':
      return {
        is_verified: true,
        is_suspended: false,
        payment_blocked: true,
        remove_earnings: false,
      }
    case 'banAndRemoveEarnings':
      return {
        is_verified: false,
        is_suspended: true,
        payment_blocked: false,
        remove_earnings: true,
      }
    case 'notApproved':
      return {
        is_verified: false,
        is_suspended: false,
        payment_blocked: false,
        remove_earnings: false,
      }
  }
}

const deriveBankType = (raw: CreatorExtraInfoRaw): CreatorExtraInfo['bankType'] => {
  const country = raw.bank_info?.bank_country
  if (country === 'GB' && !raw.bank_info?.iban) {
    return 'SORT'
  }
  if ((country === 'SE' || country === 'US' || country === 'AU') && !raw.bank_info?.iban) {
    return 'CLEARING'
  }
  return 'IBAN'
}

export const normalizeCreatorExtraInfo = (raw: CreatorExtraInfoRaw): CreatorExtraInfo => ({
  country: raw.country ?? undefined,
  city: raw.city ?? undefined,
  address: raw.address ?? undefined,
  postcode: raw.postcode ?? undefined,
  businessType: raw.businessType ?? 'INDIVIDUAL',
  ssn: raw.SSN ?? undefined,
  vatNo: raw.vat_no ?? undefined,
  company: {
    name: raw.company?.name ?? undefined,
    organisationId: raw.company?.orginisation_id ?? undefined,
    vat: raw.company?.vat ?? undefined,
  },
  bankInfo: {
    accountNumber: raw.bank_info?.account_number ?? undefined,
    bankCode: raw.bank_info?.bank_code ?? undefined,
    bankName: raw.bank_info?.bank_name ?? undefined,
    bankCountry: raw.bank_info?.bank_country ?? undefined,
    bic: raw.bank_info?.bic ?? undefined,
    iban: raw.bank_info?.iban ?? undefined,
  },
  bankType: deriveBankType(raw),
  myPageSlug: raw.my_page_slug ?? undefined,
})

export const normalizeCreatorDetail = (raw: CreatorDetailRaw): CreatorDetail => {
  const base = normalizeCreatorListItem(raw as unknown as CreatorListItemServer)

  return {
    ...base,
    phone: raw.phone,
    paymentBlocked: raw.payment_blocked,
    balance: raw.balance,
    revenueTierId: raw.revenue_tier_id ?? raw.revenue_tier.id,
    clientId: raw.client_id ?? raw.metapic_client_id ?? raw.client.id,
    metapicClientId: raw.metapic_client_id ?? raw.client.id,
    tdAccountId: raw.td_account_id,
    statusAction: deriveStatusAction(raw),
  }
}
